import React, { useState, useEffect } from "react";
import { Button, Col, Table, OverlayTrigger, Popover } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Icon, Popup } from 'semantic-ui-react';
import { useNavigate } from "react-router-dom";

import { getLMSEnrollments } from "../../Redux/Actions/enrollmentsAction";
import { cancelCheckInLearners, checkinLearners, markCompletionLearners, markAllCompletionLearners } from '../../Webservices/genericRestApis';
import LocalDateTimeDisplay from "../../utils/LocalDateTimeDisplay";
import { sortOracleRosterEnrollments } from "../../utils/sortRosterEnrollments";
import { MULTI_CLASS_SELECT_LEARNERS_KEY } from "../../constants";
import MultiClassSelectModal from "./MultiClassSelectModal";
import "./oraclelearnrostertablev2.css";
import DownloadCSV from "./downloadCSV";
import RosterLearnerCount from "./RosterLearnerCount";

const CheckMark = (props) => <Icon name="check" color="green" {...props} />;
const MinusMark = (props) => <Icon name="window minimize" color="red" {...props} />;

const ActionPopoverButton = ({ showOnCancel, onPrint, onCancel }) => {

    const popover = (
        <Popover id="action-popover" className="action-select-pop-over">
            <Popover.Content>
                <p className="h6">ACTIONS</p>
                <Button
                    className="pop-action-btn d-flex align-items-center mb-2 hover-bg"
                    onClick={onPrint}
                    variant="none"
                    disabled={true}
                >
                    <Icon name="print" className="me-2" />
                    Print Badge
                </Button>
                {showOnCancel && <Button
                    className="pop-action-btn d-flex align-items-center hover-bg"
                    onClick={onCancel}
                    variant="none"
                >
                    <Icon name="cancel" className="me-2" />
                    Cancel Check-In
                </Button>}
            </Popover.Content>
        </Popover>
    );

    return (
        <OverlayTrigger
            trigger="click"
            key={"top"}
            placement={"top"}
            overlay={popover}
            rootClose
        >
            <Icon name="cog" />
        </OverlayTrigger>
    );
};

const CheckedIn = ({ enrollment, allowCheckedIn, setAllowCheckedIn, onCancel, onCheckIn }) => {
    
    useEffect(() => {
        if (enrollment.hcmDlStatus === 'enrollment-pending' || (enrollment.hcmDlStatus === 'enrollment-success' && enrollment.checkedInDate !== null) || enrollment.hcmDlStatus.includes('completion')) {
            setAllowCheckedIn(false);
        } else if (enrollment.hcmDlStatus === 'enrollment-sent' || (enrollment.hcmDlStatus === 'enrollment-success' && enrollment.checkedInDate === null)) {
            setAllowCheckedIn(true);
        } else {
            setAllowCheckedIn(null);
        }
    }, [enrollment, setAllowCheckedIn]);

    // allowCheckedIn === null shows nothing to test if any use cases are missing
    return <>{allowCheckedIn === true ? <Button className="btn standard-btn bold" disabled={enrollment.hcmDlStatus === 'enrollment-sent'} onClick={onCheckIn}>Check-in</Button> : allowCheckedIn === false ? <Button className="btn standard-btn bold" disabled={enrollment.hcmDlStatus.includes('completion')} onClick={onCancel}>Cancel Check-in</Button> : <></>}</>
}

const Completed = ({ enrollment, onCompletion }) => {

    // if status includes 'enrollment', show a button. else, this indicates the status includes "completion-" instead, if so show an icon
    const renderBtnOrIcon = () => {

        if (enrollment.hcmDlStatus.includes("enrollment")) {

                // disabled === null was added for testing purposes, disabled should never equal null, if it does, you are missing a use case.
                const disabled = (
                enrollment.hcmDlStatus === 'enrollment-pending' || 
                enrollment.hcmDlStatus === 'enrollment-sent' || 
                (enrollment.hcmDlStatus === 'enrollment-success' && enrollment.checkedInDate === null)
                ) ? true : 
                (enrollment.hcmDlStatus === 'enrollment-success' && enrollment.checkedInDate !== null) ? false : 
                null;
                
            return (
                <Button
                    className="btn standard-btn bold"
                    onClick={onCompletion}
                    disabled={disabled}
                >
                    {disabled !== null ? "Mark Complete" : ""}
                </Button>
            );
        } else {
            let icon = null;

            if (enrollment.hcmDlStatus === 'completion-sent') {
                icon = <Popup content={""} trigger={<Icon color='red' name='minus' size='large' />} /> 
            } else if (enrollment.hcmDlStatus === 'completion-pending' || enrollment.hcmDlStatus === 'completion-success') {
                icon = <Icon color='green' name={enrollment.hcmDlStatus === 'completion-pending' ? 'question': 'checkmark'} size='large' /> 
            } else {
                // This was added for testing purposes, icon should never equal <></>, if it does, you are missing a use case.
                icon = <></>;
            }

            return icon;
        };
    }

    return <>{renderBtnOrIcon()}</>;
}

const EnrollmentRow = ({
  enrollment,
  onCancel,
  onCheckIn,
  onCompletion,
  showGearCancelCheckIn = true,
}) => {
    const [allowCheckedIn, setAllowCheckedIn] = useState(null);
    
    return (<tr>
        <td>{enrollment.name}</td>
        <td className={!enrollment?.checkedInDate ? 'text-center' : null}>{enrollment?.checkedInDate ? <>{<CheckMark />} <LocalDateTimeDisplay isoDate={enrollment?.checkedInDate} /></> : <MinusMark />}</td>

        <td>
            <CheckedIn
                enrollment={enrollment}
                allowCheckedIn={allowCheckedIn}
                setAllowCheckedIn={setAllowCheckedIn}
                onCancel={onCancel}
                onCheckIn={onCheckIn}
            />

        </td>
        <td>
            <Completed enrollment={enrollment} onCompletion={onCompletion} />
        </td>
        <td>{enrollment.walkin ? "Walk-in" : "Pre-reg"}</td>
        <td className={(enrollment.hcmDlStatus === "enrollment-success" || enrollment.hcmDlStatus === "completion-success") ? 'text-success' : 'text-danger'}>{enrollment.hcmDlStatus}</td>
        <td>
            <ActionPopoverButton
                showOnCancel={
                  showGearCancelCheckIn && !enrollment.hcmDlStatus.includes("completion")
                }
                onPrint={() => console.log('Print Badge clicked')}
                onCancel={onCancel}
            />
        </td>
    </tr>
)}

const EnrollmentDisplay = ({
  enrollments: { allCurrentEnrollments, enrollmentsFromRT },
  setIsLoading,
  showGearCancelCheckIn,
}) => {
    // const { enrollments: currentEnrollments } = useSelector(state => state.enrollments);
    const dispatch = useDispatch();


    const cancelCheckIn = async (enrollment) => {
        // This code needs to be rewritten, it is from the old code and is inefficient. 
        try {
            setIsLoading(true);

            let learnersArray = [];
            learnersArray.push(enrollment.id);
            // console.log("cancelCheckIn:Learners:learnersArray " + JSON.stringify(learnersArray));

            let course_number = sessionStorage.getItem('course_number');
            let offering_number = sessionStorage.getItem('offering_number');
            let activity_number = sessionStorage.getItem('activity_number');
            console.log("cancelCheckIn:Learners:course_number:offering_number:activity_number: " + course_number + " - " + offering_number + " - " + activity_number);

            let reqBody = {
                "courseNumber": course_number,
                "offeringNumber": offering_number,
                "activityNumber": activity_number,
                "cancel_checkin": "true",
                "mark_completion": "",
                "learners": learnersArray,
                "retrieveEnrollmentsRealtime": false,
                "realtimeEnrollmentsData": enrollmentsFromRT
            };
            console.log('cancelCheckIn reqBody: ' + JSON.stringify(reqBody));
            let response = await cancelCheckInLearners(reqBody);
            console.log('cancelCheckIn response: ' + JSON.stringify(response));
            if (response) {
                dispatch(getLMSEnrollments(response, false));
            }

        } catch (err) {
            console.error(err);
        }

        setIsLoading(false);
    }

    const performCheckIn = async (enrollment) => {
        // This code needs to be rewritten, it is from the old code and is inefficient. 
        try {
            setIsLoading(true);

            let learnersArray = [];
            learnersArray.push(enrollment.id);

            let course_number = sessionStorage.getItem('course_number');
            let offering_number = sessionStorage.getItem('offering_number');
            let activity_number = sessionStorage.getItem('activity_number');
            let offering_start_date = sessionStorage.getItem('offering_start_date');
            let offering_end_date = sessionStorage.getItem('offering_end_date');
            console.log("performCheckIn:Learners:course_number:offering_number:activity_number: " + course_number + " - " + offering_number + " - " + activity_number);

            let instructorNumber = sessionStorage.getItem('instructor_number');
            let instructorName = sessionStorage.getItem('instructor_name');

            let reqBody = {
                "courseNumber": course_number,
                "offeringNumber": offering_number,
                "activityNumber": activity_number,
                "learners": learnersArray[0],
                "offeringStartDate": offering_start_date,
                "offeringEndDate": offering_end_date,
                "name": enrollment.name,
                "enrolled": enrollment.enrolled,
                "completed": enrollment.completed,
                "activityAttemptNumber": enrollment.activityAttemptNumber,
                "completionDate": enrollment.completionDate,
                "hcmDlStatus": enrollment.hcmDlStatus,
                "hcmDlStatusMessage": enrollment.hcmDlStatusMessage,
                "instructorNumber": instructorNumber,
                "instructorName": instructorName,
                "requestId": enrollment.requestId,
                "contentId": enrollment.contentId,
                "learningRecordNumber": enrollment.learningRecordNumber,
                "assignmentNumber": enrollment.assignmentNumber,
                "retrieveEnrollmentsRealtime": false,
                "realtimeEnrollmentsData": enrollmentsFromRT
            };
            console.log('performCheckIn reqBody: ' + JSON.stringify(reqBody));
            let response = await checkinLearners(reqBody);
            //console.log('checkin response: ' + JSON.stringify(response));
            if (response) {
                dispatch(getLMSEnrollments(response, false));
            }

        } catch (err) {
            console.error(err);
        }
        setIsLoading(false);
    }

    const performCompletion = async (enrollment) => {
        try {
            setIsLoading(true);
            let learnersArray = [];

            learnersArray.push(enrollment.id);


            let course_number = sessionStorage.getItem('course_number');
            let offering_number = sessionStorage.getItem('offering_number');
            let activity_number = sessionStorage.getItem('activity_number');

            let reqBody = {
                "courseNumber": course_number,
                "offeringNumber": offering_number,
                "activityNumber": activity_number,
                "cancel_checkin": "",
                "mark_completion": "true",
                "learners": learnersArray,
                "retrieveEnrollmentsRealtime": false,
                "realtimeEnrollmentsData": enrollmentsFromRT
            };

            let response = await markCompletionLearners(reqBody);

            if (response) {
                dispatch(getLMSEnrollments(response, false));
            }

        } catch (err) {
            console.error(err);
        }

        setIsLoading(false);
    }


    return allCurrentEnrollments.map((enrollment, index) => {
        return (
          <EnrollmentRow
            key={`enrollment-${index}`}
            enrollment={enrollment}
            onCancel={() => cancelCheckIn(enrollment)}
            onCheckIn={() => performCheckIn(enrollment)}
            onCompletion={() => performCompletion(enrollment)}
            showGearCancelCheckIn={showGearCancelCheckIn}
          />
        );
    })
}

const FooterOptions = ({enrollments: { allCurrentEnrollments, allCurrentEnrollmentsLength, enrollmentsFromRT }, setIsLoading}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [showMultiClassSelect, setShowMultiClassSelect] = useState(false);

    const markAllComplete = async () => {
        try {
            setIsLoading(true);
            let course_number = sessionStorage.getItem('course_number');
            let offering_number = sessionStorage.getItem('offering_number');
            let activity_number = sessionStorage.getItem('activity_number');

            let reqBody = {
                "courseNumber": course_number,
                "offeringNumber": offering_number,
                "activityNumber": activity_number,
                "cancel_checkin": "",
                "mark_completion": "",
                "mark_all_complete": "Y",
                "learners": [],
                "retrieveEnrollmentsRealtime": false,
                "realtimeEnrollmentsData": enrollmentsFromRT
            };
            console.log('markAllComplete: ' + JSON.stringify(reqBody));
            let response = await markAllCompletionLearners(reqBody);
            console.log('markAllComplete response: ' + JSON.stringify(response));

            if (response) {
                dispatch(getLMSEnrollments(response, false));
            }

        } catch (err) {
            console.error(err);
        }
        setIsLoading(false);
    }

    const downloadData = (allCurrentEnrollments ?? [])
      .filter((enrl) => enrl.completed === true)
      .map((enrl) => enrl.id);

    const csvFileName = "Person Number List (PNL) " + sessionStorage.getItem('course_number') 
        + '_' + sessionStorage.getItem('offering_number') + '_' + sessionStorage.getItem('activity_number'); 

    const handleConfirmMultiClassSelect = ({ learners }) => {
      try {
        sessionStorage.setItem(MULTI_CLASS_SELECT_LEARNERS_KEY, JSON.stringify(learners || []));
      } catch (e) {
        console.error(e);
      }
      setShowMultiClassSelect(false);
      navigate("/selectRoster", {
        replace: true,
        state: { multiClassSelectLearners: learners || [] },
      });
    };

    const hasEnrollmentSuccessStatus = (allCurrentEnrollments ?? []).some(
      (e) => e.hcmDlStatus === "enrollment-success"
    );
    const markAllCompleteDisabled =
      !allCurrentEnrollmentsLength || !hasEnrollmentSuccessStatus;

    return (
      <>
        <div className="d-flex justify-content-end">
          <Button
            className="btn standard-btn bold mr-2"
            onClick={() => setShowMultiClassSelect(true)}
            disabled={!allCurrentEnrollmentsLength}
          >
            Add Learners to Another Class
          </Button>

          <DownloadCSV
            data={downloadData}
            fileName={csvFileName}
            className="btn standard-btn bold mr-2"
            disabled={!allCurrentEnrollmentsLength}
          >
            Download Roster Report
          </DownloadCSV>

          <Button
            className="btn standard-btn bold"
            onClick={markAllComplete}
            disabled={markAllCompleteDisabled}
          >
            Mark All Complete
          </Button>
        </div>

        <MultiClassSelectModal
          show={showMultiClassSelect}
          onClose={() => setShowMultiClassSelect(false)}
          onConfirm={handleConfirmMultiClassSelect}
          enrollments={allCurrentEnrollments || []}
        />
      </>
    );
}

const EmptyRowWithDialogue = ({ children }) => <tr>
    <td colSpan={6} className="text-center text-muted">
        {children}
    </td>
</tr>

export default function OraclelearnRosterTableV2({
  setIsLoading,
  enrollmentsLoading,
  showGearCancelCheckIn = true,
}) {
    const { enrollments: currentEnrollments } = useSelector(state => state.enrollments);

    // Display order: enrollment statuses → completion statuses, then latest check-in first within status.
    const allCurrentEnrollments = sortOracleRosterEnrollments(currentEnrollments?.data?.enrollments);
    const allCurrentEnrollmentsLength = allCurrentEnrollments?.length;
    const enrollmentsFromRT = currentEnrollments?.data.enrollments_from_rt;

    return (
        <Col md={9} className="mb-4">
            <RosterLearnerCount count={allCurrentEnrollmentsLength} />
            <Table responsive className="learner-display border">
                <thead className="table-head bg-light">
                    <tr>
                        <th>Name</th>
                        <th>Check-In Date</th>
                        <th>Checked In?</th>
                        <th>Complete?</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {enrollmentsLoading ? <EmptyRowWithDialogue>Loading...</EmptyRowWithDialogue>: allCurrentEnrollmentsLength > 0 ? <EnrollmentDisplay enrollments={{ allCurrentEnrollments, enrollmentsFromRT }} setIsLoading={setIsLoading} showGearCancelCheckIn={showGearCancelCheckIn} /> :
                       <EmptyRowWithDialogue>
                            Enrollments will display below if found. You can continue learner check-ins while the enrollments are loading.
                        </EmptyRowWithDialogue>}
                </tbody>
            </Table>
            <RosterLearnerCount count={allCurrentEnrollmentsLength} />
            <FooterOptions enrollments={{ allCurrentEnrollments, allCurrentEnrollmentsLength, enrollmentsFromRT }} setIsLoading={setIsLoading} />
        </Col>
    )
};